import {
  DataGrid,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridValidRowModel,
} from "@mui/x-data-grid";

interface CustomizedDataGridProps {
  onCellClick?: (params: GridCellParams) => void;
  columns: GridColDef[];
  data: GridValidRowModel[] | undefined;
  isLoading: boolean;
  initialSortingField: string;
  rowSelection: boolean;
  type?: "batch" | "claim" | "client" | "task" | "folder" | "file";
  height?: number;
  hideFooter?: boolean;
  pageSize?: number;
  getRowId?: (row: GridValidRowModel) => GridRowId;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowSelectionModelChange?: (
    rowSelectionModel: GridRowSelectionModel,
    details: GridCallbackDetails,
  ) => void;
  isRowSelectable?: (params: GridRowParams) => boolean;
}

const CustomizedDataGrid = ({
  data,
  columns,
  onCellClick = () => {},
  isLoading,
  rowSelection,
  type,
  initialSortingField,
  height = 455,
  hideFooter = false,
  pageSize = 10,
  getRowId,
  checkboxSelection,
  rowSelectionModel,
  onRowSelectionModelChange,
  isRowSelectable,
}: CustomizedDataGridProps) => {
  return (
    <DataGrid
      data-testid={`customized-data-grid-${type}`}
      onCellClick={onCellClick}
      rows={data}
      columns={columns}
      getRowId={getRowId}
      checkboxSelection={checkboxSelection}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={onRowSelectionModelChange}
      isRowSelectable={isRowSelectable}
      hideFooter={hideFooter}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
      }
      initialState={{
        pagination: {
          paginationModel: { pageSize: hideFooter ? 100 : pageSize },
        },
        sorting: {
          sortModel: [{ field: initialSortingField, sort: "desc" }],
        },
      }}
      sx={{
        height,
        width: "100%",
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 2,
        overflow: "hidden",
        "& .MuiDataGrid-main": {
          borderRadius: 2,
          overflow: "hidden",
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "grey.50",
          borderBottom: "2px solid",
          borderColor: "grey.300",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "text.primary",
        },
        "& .MuiDataGrid-columnHeader": {
          padding: "12px 16px",
          "&:focus, &:focus-within": {
            outline: "none",
          },
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: 600,
          fontSize: "0.875rem",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        },
        "& .MuiDataGrid-cell": {
          fontSize: "0.875rem",
          padding: "12px 16px",
          borderColor: "grey.200",
          color: "text.primary",
          lineHeight: 1.5,
          "&:focus, &:focus-within": {
            outline: "none",
          },
        },
        "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox": {
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
        "& .MuiDataGrid-row": {
          minHeight: "52px !important",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(25, 118, 210, 0.08)",
            cursor: rowSelection ? "pointer" : "default",
            boxShadow: rowSelection ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
            "& .MuiDataGrid-cell": {
              color: "text.primary",
              fontWeight: 500,
            },
          },
          "&.even": {
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },
          },
          "&.odd": {
            backgroundColor: "background.paper",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.08)",
            },
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(25, 118, 210, 0.12)",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.16)",
            },
            "& .MuiDataGrid-cell": {
              color: "text.primary",
              fontWeight: 500,
            },
          },
        },
        "& .MuiDataGrid-virtualScroller": {
          overflow: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
            width: 0,
            height: 0,
          },
          scrollbarWidth: "none",
          "-ms-overflow-style": "none",
        },
        "& .MuiDataGrid-virtualScrollerContent": {
          "&::-webkit-scrollbar": {
            display: "none",
          },
        },
        "& .MuiDataGrid-scrollbar": {
          display: "none !important",
        },
        "& .MuiDataGrid-scrollbar--vertical": {
          display: "none !important",
        },
        "& .MuiDataGrid-scrollbar--horizontal": {
          display: "none !important",
        },
        "& .MuiDataGrid-footerContainer": {
          borderTop: "2px solid",
          borderColor: "grey.300",
          backgroundColor: "grey.50",
          minHeight: 52,
          overflow: "hidden",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          "-ms-overflow-style": "none",
        },
        "& .MuiTablePagination-root": {
          color: "text.secondary",
          fontSize: "0.875rem",
          overflow: "hidden",
        },
        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
          {
            fontSize: "0.875rem",
            fontWeight: 500,
          },
        "& .MuiDataGrid-selectedRowCount": {
          fontSize: "0.875rem",
          fontWeight: 500,
        },
      }}
      // 100 is the largest page size the MIT DataGrid allows; anything bigger throws.
      pageSizeOptions={[10, 15, 25, 50, 100]}
      disableColumnResize
      rowHeight={52}
      disableRowSelectionOnClick={!rowSelection}
      loading={isLoading}
      slotProps={{
        filterPanel: {
          filterFormProps: {
            logicOperatorInputProps: {
              variant: "outlined",
              size: "small",
            },
            columnInputProps: {
              variant: "outlined",
              size: "small",
              sx: { mt: "auto" },
            },
            operatorInputProps: {
              variant: "outlined",
              size: "small",
              sx: { mt: "auto" },
            },
            valueInputProps: {
              InputComponentProps: {
                variant: "outlined",
                size: "small",
              },
              columnInputProps: {
                variant: "outlined",
                size: "small",
                sx: { mt: "auto" },
              },
              operatorInputProps: {
                variant: "outlined",
                size: "small",
                sx: { mt: "auto" },
              },
              valueInputProps: {
                InputComponentProps: {
                  variant: "outlined",
                  size: "small",
                },
              },
            },
          },
        },
      }}
    />
  );
};

export default CustomizedDataGrid;
